import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  EAuthValidationKey,
  ELocalStorageKeys,
  GetAllPersonsByUserResponse,
  hasAuthValidationError,
  IAuthAccessToken,
  IAuthLoginPayload,
  isSupportSessionToken,
} from './entities/authentication.interface';
import { buildRedirectUriWithToken, isAllowedRedirectUri } from './auth-redirect.util';
import { computed, inject, Injector } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, retry, switchMap, exhaustMap, tap, take, EMPTY } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { Encrypt } from '@shared/utils';
import { LocalStorageFacade } from '@application/localstorage';
import { AuthService } from './infra/auth.service';
import { IPerson, PersonService } from '@domains/person/register/data';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  APP_CONFIG,
  App_url_type_by_AppSource_enum,
  AppSource,
  AppUrlOptions,
  resolveAuthOrigin,
} from '@application/config';
import { AlertStore } from '@alert/data-access';
import { WebAuthnService } from './infra/web-authn.service';
import { User } from './entities/user';
import { ServerMockService } from './infra/server-mock.service';
import { RouterStore } from '@route/data-access';
import { Capacitor } from '@capacitor/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { LanguageStore } from '@settings/language/data-access';
import { languageCodeMap } from '@shared/components/shortcuts/data-access';

import { DateTime } from 'luxon';

export type IRememberEmail = {
  Email: string;
  PersonId: string;
};

export interface IAuthStore {
  authData: IAuthAccessToken | null;
  webauthn: any;
  tempLoginPayload: IAuthLoginPayload;
  emailToLogin: string;
  rememberNextLoginEmail: boolean;
  redirectToApp: AppUrlOptions;
  redirectUri: string | null;
  selectedRememberedAccount: any;
  allPersonsByUser: GetAllPersonsByUserResponse[];
  allAccountEmails: IRememberEmail[];
  allowUserInputCredentials: boolean;
  tempAuthData: IAuthAccessToken;
  temporaryAuthBackup: IAuthAccessToken | null;
  temporaryAuthExpiresAt: number | null;
  isTemporaryAuthActive: boolean;
  person: IPerson;
  sessionEnded: boolean;
}

export const initialAuthStore: IAuthStore = {
  authData: null,
  webauthn: null,
  tempLoginPayload: null,
  emailToLogin: null,
  rememberNextLoginEmail: false,
  redirectToApp: null,
  redirectUri: null,
  selectedRememberedAccount: null,
  allPersonsByUser: [],
  allAccountEmails: [],
  allowUserInputCredentials: true,
  tempAuthData: null,
  temporaryAuthBackup: null,
  temporaryAuthExpiresAt: null,
  isTemporaryAuthActive: false,
  person: null,
  sessionEnded: false,
};

export const AuthStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialAuthStore),
  withComputed((state, observe = toObservable(state.authData)) => ({
    getAuthData: computed(() => state.authData()),
    isLoggedIn: computed(() => !!state.authData()),
    isSupportSession: computed(() => isSupportSessionToken(state.authData())),
    getTempLoginPayload: computed(() => state.tempLoginPayload()),
    getEmailToLogin: computed(() => state.emailToLogin()),
    observableAuthData: computed(() => observe),
  })),
  withMethods(
    (
      state,
      injector = inject(Injector),
      crypt = inject(Encrypt),
      localStorageFacade = inject(LocalStorageFacade),
      alertStore = inject(AlertStore),
      webAuthnService = inject(WebAuthnService),
      serverMockService = inject(ServerMockService),
      appConfig = inject(APP_CONFIG),
      routerStore = inject(RouterStore),
      errorStore = inject(ErrorStore),
      personService = inject(PersonService)
    ) => {
      const defaultOrigin = resolveAuthOrigin(appConfig);
      const getAuthService = () => injector.get(AuthService);
      // Resolved lazily, never during this factory: `LanguageStore.onInit` calls
      // `TranslateService.use()`, which subscribes to the loader synchronously and
      // fires the i18n `HttpClient.get`. That runs the whole interceptor chain —
      // including `TokenInterceptor`, which injects `AuthStore` — while `AuthStore`
      // is still being constructed, closing a DI cycle (NG0200). By the time a
      // language change is requested this store is long constructed.
      const getLanguageStore = () => injector.get(LanguageStore);

      let temporaryAuthTimeout: ReturnType<typeof setTimeout> | null = null;

      const clearTemporaryAuthTimeout = () => {
        if (temporaryAuthTimeout) {
          clearTimeout(temporaryAuthTimeout);
          temporaryAuthTimeout = null;
        }
      };

      const restoreTemporaryAuth = () => {
        const backup = state.temporaryAuthBackup();
        clearTemporaryAuthTimeout();

        if (!backup) {
          patchState(state, {
            isTemporaryAuthActive: false,
            temporaryAuthBackup: null,
            temporaryAuthExpiresAt: null,
          });
          return;
        }

        patchState(state, {
          authData: backup,
          isTemporaryAuthActive: false,
          temporaryAuthBackup: null,
          temporaryAuthExpiresAt: null,
        });
      };

      const scheduleTemporaryAuthCleanup = (duration: number) => {
        clearTemporaryAuthTimeout();
        if (duration <= 0) {
          restoreTemporaryAuth();
          return;
        }

        temporaryAuthTimeout = setTimeout(() => {
          restoreTemporaryAuth();
        }, duration);
      };

      const getAppSourceEnvironment = (appSource: AppSource): AppUrlOptions => {
        return App_url_type_by_AppSource_enum[
          AppSource[appSource] as keyof typeof App_url_type_by_AppSource_enum
        ] as AppUrlOptions;
      };

      const getSessionUserNameFromStorage = (authData: IAuthAccessToken): string => {
        try {
          const activeStr = localStorageFacade.getItemRaw('activeAccount');
          if (activeStr) {
            const active = JSON.parse(activeStr);
            if (active?.PersonId === authData.PersonId && active?.FirstName) {
              return active.FirstName;
            }
          }
        } catch {
          // ignore
        }
        const person = state.allPersonsByUser()?.find(
          (p: GetAllPersonsByUserResponse) => p.PersonId === authData.PersonId
        );
        return person?.FirstName ?? '';
      };

      /**
       * Grava o nome de exibicao na chave crua `userName` — e o recurso de que
       * `PairingSessionService.connectedAccountName()` depende quando ainda nao
       * ha `activeAccount` gravado. Sem nome, remove a chave em vez de gravar um
       * marcador: o pareamento mostraria o marcador como se fosse a conta.
       */
      const rememberSessionUserName = (name: string) => {
        const trimmed = name.trim();
        try {
          if (trimmed) {
            localStorageFacade.setItemRaw('userName', trimmed);
          } else {
            localStorageFacade.removeItemRaw('userName');
          }
        } catch {
          // armazenamento bloqueado — o pareamento fica sem o nome
        }
      };

      const loadSessionPerson = (authData: IAuthAccessToken) => {
        // Sem PersonId o `getPerson` concatena `undefined` na query e o backend
        // responde 400 (`Person?id=undefined`).
        if (!authData?.PersonId) return;
        const nameFromStorage = getSessionUserNameFromStorage(authData);
        const personService = injector.get(PersonService);
        personService
          .getPerson(authData.PersonId)
          .pipe(take(1))
          .subscribe({
            next: (person) => {
              const name =
                person?.FirstName ?? person?.DisplayName ?? person?.FullName ?? nameFromStorage;
              rememberSessionUserName(name);
              patchState(state, { person });
            },
            error: () => {
              // person fica nulo; a UI reage por getPersonLoading()/person()
              rememberSessionUserName(nameFromStorage);
            },
          });
      };

      const clearSession = () => {
        clearTemporaryAuthTimeout();
        localStorageFacade.removeItemRaw('userName');
        localStorageFacade.removeItem(ELocalStorageKeys.auth);
        patchState(state, { authData: null, redirectUri: null });
        localStorageFacade.removeItem(ELocalStorageKeys.webauthn);
      };

      const logout = (sessionEnded = false) => {
        if (sessionEnded) {
          patchState(state, { sessionEnded: true });
        }
        clearSession();
        if (Capacitor.isNativePlatform()) {
          routerStore.navigate(['/onboarding']);
          return;
        }

        const sessionEndedQuery = state.sessionEnded() ? '&sessionEnded=true' : '';

        if (!location.href.includes('localhost')) {
          const appUrl = `${window.location.protocol}//accounts.${location.href
            .split('.')
            .slice(1)
            .join('.')}`;
          location.href =
            `${appUrl}` +
            `?next=${getAppSourceEnvironment(appConfig.appSource)}` +
            sessionEndedQuery;
        } else {
          location.href =
            appConfig.appUrl.accounts +
            `?next=${getAppSourceEnvironment(appConfig.appSource)}` +
            sessionEndedQuery;
        }
      };

      const transportBearerTo = (app: AppUrlOptions, data: any) => {
        if (!data) {
          return;
        }

        const base64 = window.btoa(JSON.stringify(data));
        // Native bundle (Capacitor): keep the session in-app via a relative path. It
        // lands on the app's own origin regardless of the WebView scheme
        // (capacitor://localhost, https://localhost, or live-reload localhost:PORT),
        // so the bearer never escapes to the external web app. Building an absolute URL
        // from window.location.origin is unsafe here: under a custom scheme that origin
        // is opaque, which breaks the redirect-uri validation and falls through below.
        // The Ediz guardian app (EdizApp) is self-contained (login + home in one
        // app), so it keeps the session local like native instead of redirecting
        // to the separate Ediz management app.
        // The access remote control is self-contained too, and on the web it has
        // no sibling app to hand the session to: `goRedirect` below derives the
        // target from the page address, which yields the main system on a real
        // domain and somebody else's site on a shared one such as `pages.dev`.
        if (
          (Capacitor.isNativePlatform() ||
            appConfig.appSource === AppSource.EdizApp ||
            appConfig.app_id === 'accessremotecontrol') &&
          !state.redirectUri()
        ) {
          window.location.href = `/transmission?token=${base64}`;
          return;
        }
        const redirectUri = buildRedirectUriWithToken(state.redirectUri() ?? null, base64);
        if (redirectUri) {
          window.location.href = redirectUri;
          return;
        }

        const goRedirect = () => {
          if (!window.location.href.includes('localhost')) {
            let currentHostname = window.location.href
              .split('.')
              .slice(1)
              .join('.');

            if (currentHostname.split('/').length > 1) {
              currentHostname = currentHostname.split('/')[0];
            }
            let appUrl;
            if (
              currentHostname.includes('.dev') ||
              defaultOrigin === 'ediz' ||
              defaultOrigin === 'tickou'
            ) {
              appUrl = `${window.location.protocol}//app.${currentHostname}`;
            } else {
              appUrl = `${window.location.protocol}//novo.${currentHostname}`;
            }

            window.location.href = `${appUrl}/transmission?token=${base64}`;
          } else {
            const appUrl =
              defaultOrigin === 'ediz'
                ? appConfig.appUrl.ediz_app
                : defaultOrigin === 'tickou'
                  ? appConfig.appUrl.tickou_app
                  : appConfig.appUrl.actuar_app;
            window.location.href = `${appUrl}/transmission?token=${base64}`;
          }
        };

        if (!app) {
          app = getAppSourceEnvironment(state.allPersonsByUser()[0].AppSource);
          goRedirect();
          return;
        }

        goRedirect();
      };

      const loadAllPersonsByUser = rxMethod<any>(
        pipe(
          switchMap((onComplete) =>
            getAuthService()
              .getAllPersonsByUser()
              .pipe(
                tap(() => {
                  patchState(state, {
                    ...setPropLoading('loadAllPersonsByUser'),
                  });
                }),
                tapResponse({
                  next: (result) => {
                    const email = state.tempLoginPayload()?.Email;
                    if (email) {
                      result.forEach((person) => {
                        person.Email = email;
                      });
                    }
                    patchState(state, {
                      allPersonsByUser: result,
                      ...setPropLoaded('loadAllPersonsByUser'),
                    });
                    onComplete?.();
                  },
                  error: (e) => {
                    patchState(state, {
                      allPersonsByUser: [],
                      ...setPropError('loadAllPersonsByUser', e),
                    });
                  },
                })
              )
          )
        )
      );

      const addEmailAccountLocalStorage = (payload: IRememberEmail) => {
        const rememberEmail =
          JSON.parse(localStorageFacade.getItem(ELocalStorageKeys.rememberEmail)) ||
          [];
        if (
          rememberEmail?.find((e: IRememberEmail) => e.Email === payload.Email)
        ) {
          return;
        }
        localStorageFacade.setItem(
          ELocalStorageKeys.rememberEmail,
          JSON.stringify([...rememberEmail, payload])
        );
      };

      const removeEmailAccountLocalStorage = (email: string) => {
        const rememberEmail =
          JSON.parse(localStorageFacade.getItem(ELocalStorageKeys.rememberEmail)) ||
          [];
        const newRememberEmail = rememberEmail.filter(
          (e: IRememberEmail) => e.Email !== email
        );
        localStorageFacade.setItem(
          ELocalStorageKeys.rememberEmail,
          JSON.stringify(newRememberEmail)
        );
        patchState(state, { allAccountEmails: newRememberEmail });
      };

      const changePersonAccount = rxMethod<{
        PersonId: string;
        onComplete?: () => void;
        onSupportAccessDenied?: () => void;
      }>(
        pipe(
          tap(() => {
            patchState(state, { ...setPropLoading('changePersonAccount') });
          }),
          switchMap(({ PersonId, onComplete, onSupportAccessDenied }) =>
            getAuthService()
              .changePersonAccount(PersonId)
              .pipe(
                tapResponse({
                  next: (authData) => {
                    localStorageFacade.setItem(
                      ELocalStorageKeys.auth,
                      JSON.stringify(authData)
                    );
                    patchState(state, {
                      authData,
                      ...setPropLoaded('changePersonAccount'),
                    });
                    loadSessionPerson(authData);
                    onComplete?.();
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('changePersonAccount', e),
                    });
                    if (
                      isSupportSessionToken(state.authData()) &&
                      hasAuthValidationError(e, EAuthValidationKey.Access)
                    ) {
                      errorStore.handleError({
                        title: 'ACCOUNT.SUPPORT_SESSION.CHANGE_UNIT_DENIED.TITLE',
                        message: 'ACCOUNT.SUPPORT_SESSION.CHANGE_UNIT_DENIED.MESSAGE',
                      });
                      onSupportAccessDenied?.();
                    }
                  },
                })
              )
          )
        )
      );

      const updateLanguageBySettings = rxMethod<void>(
        pipe(
          switchMap(() =>
            getAuthService()
              .getUserSettings()
              .pipe(
                tapResponse({
                  next: (userSettings) => {
                    const language = languageCodeMap[userSettings?.LanguageCode];
                    if (language) {
                      getLanguageStore().setLanguage(language);
                    }
                  },
                  error: () => {},
                })
              )
          )
        )
      );

      const login = rxMethod<IAuthLoginPayload>(
        pipe(
          tap(() => {
            patchState(state, { ...setPropLoading('doLogin') });
          }),
          switchMap((loginForm) =>
            getAuthService()
              .login(loginForm)
              .pipe(
                tapResponse({
                  next: (authData) => {
                    patchState(state, {
                      authData,
                      tempAuthData: authData,
                      tempLoginPayload: loginForm,
                      sessionEnded: false,
                      ...setPropLoaded('doLogin'),
                    });

                    if (state.rememberNextLoginEmail()) {
                      const emailAccount = {
                        Email: loginForm.Email,
                        PersonId: authData.PersonId,
                      };
                      addEmailAccountLocalStorage(emailAccount);
                      if (
                        !state
                          .allAccountEmails()
                          .find(
                            (e: IRememberEmail) =>
                              e.Email === emailAccount.Email
                          )
                      ) {
                        patchState(state, {
                          allAccountEmails: [
                            ...state.allAccountEmails(),
                            emailAccount,
                          ],
                        });
                      }
                    }

                    const thenLoadAllPersonsByUser = () => {
                      if (state.allPersonsByUser().length === 1) {
                        const singlePerson = state.allPersonsByUser()[0];
                        changePersonAccount({
                          PersonId: singlePerson.PersonId,
                          onComplete: () => {
                            transportBearerTo('actuar_app', state.authData());
                          },
                        });
                        return;
                      }
                      routerStore.navigate(['/auth/user-selection']);
                    };

                    loadAllPersonsByUser(thenLoadAllPersonsByUser);
                    if (appConfig.appSource !== AppSource.EdizApp) {
                      updateLanguageBySettings();
                    }
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(state, {
                      authData: null,
                      ...setPropError('doLogin', e),
                    });

                    const passwordErrors =
                      e?.error?.Erros?.Password ?? e?.error?.Errors?.Password;
                    const isTemporaryPasswordError =
                      e.status === 400 &&
                      Array.isArray(passwordErrors) &&
                      passwordErrors.includes(
                        'Localize.UserService.Msg.TemporaryPassword'
                      );

                    if (isTemporaryPasswordError) {
                      patchState(state, { tempLoginPayload: { ...loginForm } });
                      routerStore.navigate(['/change-temporary-password']);
                    }
                  },
                })
              )
          )
        )
      );
      const loginWebAuthn = (user: User[]) => {
        webAuthnService
          .webAuthnSignin(user)
          .then(() => {
            const loginForm: IAuthLoginPayload = {
              Email: user[0].email,
              Password: user[0].password,
              Origin: (user[0].origin as any) || defaultOrigin,
            };
            return login(loginForm);
          })
          .catch((error) => {
            console.error('FAIL', error);
          });
      };
      const setAuthData = (data: IAuthAccessToken) => {
        patchState(state, { authData: data });
        loadSessionPerson(data);
      };
      return {
        setAuthData,
        beginTemporaryAuthSession: (
          authData: IAuthAccessToken,
          durationMs = 30_000
        ) => {
          if (!authData?.Token) {
            return;
          }

          const backup = state.temporaryAuthBackup() ?? state.authData();
          patchState(state, {
            authData,
            temporaryAuthBackup: backup,
            isTemporaryAuthActive: true,
            temporaryAuthExpiresAt: DateTime.now().toMillis() + durationMs,
          });

          scheduleTemporaryAuthCleanup(durationMs);
        },
        endTemporaryAuthSession: () => {
          restoreTemporaryAuth();
        },
        doLogin: (loginForm: IAuthLoginPayload) => {
          loginForm.Email = loginForm.Email.toLowerCase();
          loginForm.Password = crypt.encryptMd5(
            loginForm.Email + loginForm.Password
          );
          loginForm.Origin = loginForm.Origin || defaultOrigin;
          return login(loginForm);
        },
        doRedirectToApp: () => {
          transportBearerTo('actuar_app', state.authData());
        },
        userHasWebAuthnCredentials: () => {
          return !!localStorageFacade.getItem('credentials');
        },
        eraseWebauthnCredentials: () => {
          localStorageFacade.removeItem('credentials');
        },
        selectAccountAndRedirect: (account: any) => {
          changePersonAccount({
            PersonId: account.PersonId,
            onComplete: () => {
              transportBearerTo('actuar_app', state.authData());
            },
          });
        },
        registerWebAuthn: () => {
          const loginForm = state.tempLoginPayload();
          const authData = state.authData();
          if (!loginForm || !authData) {
            return;
          }
          const user: User = {
            id: loginForm.Email,
            email: loginForm.Email,
            credentials: [],
            password: loginForm.Password,
          };
          webAuthnService
            .webAuthnSignup(user)
            .then((credential: PublicKeyCredential) => {
              const valid = serverMockService.registerCredential(
                user,
                credential
              );
              if (valid) {
                transportBearerTo('actuar_app', state.authData());
              }
            })
            .catch((error) => {
              alertStore.create({
                message: 'LOGIN.BIOMETRICS.FAILED_TO_CREATE_BIOMETRY',
                title: 'LOGIN.BIOMETRICS.ALERT_TITLE_CREATE_BIOMETRY',
                type: 'warning',
              });
            });
        },
        loginWebAuthn: () => {
          const user = JSON.parse(localStorageFacade.getItem('credentials'));
          loginWebAuthn(user);
        },
        setEmailLogin(emailToLogin: string) {
          patchState(state, { emailToLogin });
        },
        setRemeberedAccountToLogin(account: any) {
          patchState(state, { selectedRememberedAccount: account });
        },
        eraseLocalAllPersonsByUser() {
          localStorageFacade.removeItem('allPersonsByUser');
          patchState(state, { allPersonsByUser: [] });
        },
        removeOneRememberedAccount(email: string) {
          removeEmailAccountLocalStorage(email);
        },
        setAllowUserInputCredentials(allow: boolean) {
          patchState(state, { allowUserInputCredentials: allow });
        },
        setTempLoginPayload(tempLoginPayload: IAuthLoginPayload) {
          patchState(state, { tempLoginPayload });
        },
        preferPassword: (password: boolean) => {
          localStorageFacade.setItem('password', password);
        },
        setRedirectToApp(redirectToApp: AppUrlOptions) {
          patchState(state, { redirectToApp: redirectToApp });
        },
        setRedirectUri(redirectUri: string | null) {
          const normalizedRedirectUri = redirectUri?.trim() || null;
          patchState(state, {
            redirectUri: isAllowedRedirectUri(normalizedRedirectUri)
              ? normalizedRedirectUri
              : null
          });
        },
        clearRedirectUri() {
          patchState(state, { redirectUri: null });
        },
        rememberLoginEmail(rememberNextLoginEmail: boolean) {
          patchState(state, { rememberNextLoginEmail });
        },
        loginWithTempData: () => login(state.tempLoginPayload()),
        refreshToken: rxMethod<void>(
          pipe(
            // Cada grade OData chama isto no seu 401 — várias na mesma tela, como
            // a de hotsites. `switchMap` abortava o refresh anterior quando o
            // servidor já podia ter rodado o `TokenId`, e o seguinte ia com ele
            // consumido: 400 e logout. Enquanto um corre, os outros esperam por
            // ele (as grades reagem ao `authData` novo, não a esta chamada).
            exhaustMap(() => {
              const tokenId = state.authData()?.TokenId;
              if (!tokenId) {
                logout();
                return EMPTY;
              }
              return getAuthService()
                .refreshToken(tokenId)
                .pipe(
                  retry(2),
                  tapResponse({
                    next: (authData) => {
                      patchState(state, {
                        authData,
                        ...setPropLoaded('refreshToken'),
                      });
                      localStorageFacade.setItem(
                        ELocalStorageKeys.auth,
                        JSON.stringify(authData)
                      );
                      loadSessionPerson(authData);
                      if (authData.TokenId.includes('0000')) {
                        logout();
                      }
                    },
                    error: () => logout(),
                  })
                )
            })
          )
        ),
        getPerson: rxMethod<string>(
        pipe(
        tap(() =>  patchState(state, { ...setPropLoading('getPerson')})),
        switchMap((payload) => {
            return personService
          .getPerson(payload).pipe(
            tapResponse({
                next: (person: IPerson) => {
                patchState(state, {
                    person,
                    ...setPropLoaded('getPerson'),
                });
                },
                error: (error: HttpErrorResponse) => {
                patchState(state, {
                    ...setPropError('getPerson', error),
                });
                },
            })
            );
        })
        )
    ),
          
        loginByCodeTemp: rxMethod<{ sid: string; uid: string }>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('loginByCodeTemp') })
            ),
            switchMap(({ sid, uid }) => {
              return getAuthService()
                .loginByCodeTemp({ sid, uid })
                .pipe(
                  tapResponse({
                    next: (authData) => {
                      setAuthData(authData);
                      patchState(state, { tempAuthData: authData });

                      getAuthService()
                        .getAllPersonsByUser()
                        .pipe(
                          tapResponse({
                            next: (result) => {
                              patchState(state, { allPersonsByUser: result });

                              if (result.length === 1) {
                                changePersonAccount({
                                  PersonId: result[0].PersonId,
                                  onComplete: () => {
                                    transportBearerTo('actuar_app', state.authData());
                                  },
                                });
                              } else {
                                transportBearerTo('actuar_app', authData);
                              }
                            },
                            error: (e) => {
                              console.error(e);
                              transportBearerTo('actuar_app', authData);
                            },
                          })
                        )
                        .subscribe();

                      patchState(state, {
                        ...setPropLoaded('loginByCodeTemp'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(state, {
                        ...setPropError('loginByCodeTemp', error),
                      });
                    },
                  })
                );
            })
          )
        ),
        changePersonAccount: changePersonAccount,
        logout(sessionEnded = false) {
          logout(sessionEnded);
        },
        markSessionEnded() {
          patchState(state, { sessionEnded: true });
        },
        announceServerSelectedUnit(onUnitResolved: (unitName: string) => void) {
          loadAllPersonsByUser(() => {
            const unit = state
              .allPersonsByUser()
              .find((person) => person.PersonId === state.authData()?.PersonId);
            if (unit?.TenantName) {
              onUnitResolved(unit.TenantName);
            }
          });
        },
        clearSession() {
          clearSession();
        },
        resetReqState: () => {
          patchState(state, {
            ...setPropInit(
              'doLogin',
              'refreshToken',
              'loginWithTempData',
              'changePersonAccount',
              'loadAllPersonsByUser'
            ),
          });
        },
      };
    }
  ),
  withHooks({
    onInit: (state) => {
      const localStorageFacade = inject(LocalStorageFacade);
      const authData = JSON.parse(localStorageFacade.getItem(ELocalStorageKeys.auth));
      const webauthn = JSON.parse(
        localStorageFacade.getItem(ELocalStorageKeys.webauthn)
      );
      const allPersonsByUser =
        JSON.parse(localStorageFacade.getItem('allPersonsByUser')) || [];
      const rememberEmail =
        JSON.parse(localStorageFacade.getItem(ELocalStorageKeys.rememberEmail)) || [];
      if (authData) {
        patchState(state, { authData });
      }
      if (webauthn) {
        patchState(state, { webauthn, emailToLogin: webauthn[0].email });
      }
      if (allPersonsByUser) {
        patchState(state, { allPersonsByUser });
      }
      if (rememberEmail) {
        patchState(state, {
          allAccountEmails: rememberEmail,
          allowUserInputCredentials: false,
        });
      }
    },
  }),
  withReqState(
    'doLogin',
    'refreshToken',
    'loginWithTempData',
    'changePersonAccount',
    'loadAllPersonsByUser',
    'loginByCodeTemp',
    'getPerson'
  )
);
